// Create / edit a scheduled (non-job) task from the calendar. Single modal
// for both: pass a `task` to edit (adds Mark done/Reopen + Delete), or omit
// it to create. Reuses PeopleMultiSelect; a task requires ≥1 person (Save is
// disabled with none selected — server enforces too). Tasks are single-day.

import { useState } from 'react';
import { Trash2, CheckCircle2, RotateCcw } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TASK_TYPES, TASK_TYPE_LABELS, type CalendarTaskRow, type TaskType } from '@gutterinv/shared';
import {
  postScheduledTask,
  patchScheduledTask,
  deleteScheduledTask,
  setScheduledTaskComplete,
} from '../api/client.js';
import { PeopleMultiSelect } from './PeopleMultiSelect.js';
import { Button } from './ui/button.js';
import { Input } from './ui/input.js';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog.js';
import { cn } from '../lib/utils.js';
import { todayIso, taskDateFloor } from '../lib/format.js';
import { taskStyle } from '../lib/task-styles.js';

export function ScheduledTaskModal({
  task,
  defaultDate,
  onClose,
}: {
  /** Provided → edit mode; omitted → create mode. */
  task?: CalendarTaskRow | null;
  /** Pre-fill date in create mode (e.g. the clicked day). */
  defaultDate?: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const isEdit = !!task;

  const [taskType, setTaskType] = useState<TaskType>(task?.taskType ?? 'physical_inventory');
  const [taskDate, setTaskDate] = useState<string>(task?.taskDate ?? defaultDate ?? todayIso());
  const [personIds, setPersonIds] = useState<string[]>(task ? task.people.map((p) => p.id) : []);
  const [notes, setNotes] = useState<string>(task?.notes ?? '');

  // Reverting a PAST completed task is DEFERRED, not committed on click: it
  // sets local state only (doneState=false + forceTodayFloor + pendingRevert)
  // and keeps the modal open in edit mode. The actual server uncomplete fires
  // from Save, AFTER the date patch — so the task can't be left incomplete in
  // the past via Cancel/Escape/backdrop (which simply abandon the pending
  // revert, leaving the task completed at its original date). All three flags
  // re-init per open (both mount sites unmount on close).
  const [doneState, setDoneState] = useState(!!task?.completedAt);
  const [forceTodayFloor, setForceTodayFloor] = useState(false);
  const [pendingRevert, setPendingRevert] = useState(false);

  // Past-date guard (UX-only, mirrors jobs' AssignmentEditModal). Create / editing
  // a today-or-future task floors at today; editing an already-past task floors at
  // its own date so it stays editable but can't be pushed further back. After a
  // past-task REVERT, forceTodayFloor overrides that exemption → must reschedule.
  const dateFloor = forceTodayFloor ? todayIso() : taskDateFloor(isEdit, task?.taskDate, todayIso());
  const dateInvalid = taskDate < dateFloor;

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['calendar', 'tasks'] });
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = { taskType, taskDate, personIds, notes: notes.trim() || null };
      // Patch the (now today-or-future) date FIRST, then commit a deferred past
      // revert. Order matters: if the second call failed, the task stays
      // completed at the new valid date — never incomplete-in-the-past.
      const result = isEdit
        ? await patchScheduledTask(task!.id, payload)
        : await postScheduledTask(payload);
      if (pendingRevert) await setScheduledTaskComplete(task!.id, false);
      return result;
    },
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteScheduledTask(task!.id),
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  // Immediate completion toggle for the NON-deferred cases: mark-done, and
  // revert of a today/future task. A past-task revert is handled locally (see
  // the toggle onClick) and committed by Save instead.
  const completeMutation = useMutation({
    mutationFn: (next: boolean) => setScheduledTaskComplete(task!.id, next),
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  // Toggle the completion control. Past-task revert defers (local only) so it
  // can't be left incomplete-in-the-past; re-clicking a pending revert just
  // abandons it (the server was never touched); everything else commits now.
  const onToggleComplete = () => {
    if (pendingRevert) {
      onClose();
      return;
    }
    if (doneState && task!.taskDate < todayIso()) {
      setDoneState(false);
      setForceTodayFloor(true);
      setPendingRevert(true);
      return;
    }
    completeMutation.mutate(!doneState);
  };

  const noPeople = personIds.length === 0;
  const saveDisabled = saveMutation.isPending || !taskDate || dateInvalid || noPeople;
  const busy = saveMutation.isPending || deleteMutation.isPending || completeMutation.isPending;

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !busy) onClose();
      }}
    >
      <DialogContent
        hideClose
        className="w-[520px] max-w-[calc(100vw-2rem)] p-8 space-y-6"
        onEscapeKeyDown={(e) => {
          if (busy) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (busy) e.preventDefault();
        }}
      >
        <div className="flex items-center justify-between gap-3">
          <DialogTitle className="text-base font-semibold text-foreground">
            {isEdit ? 'Edit task' : 'New task'}
          </DialogTitle>
          {isEdit && (
            <Button
              variant="outline"
              disabled={busy}
              onClick={onToggleComplete}
              title={doneState ? 'Reopen this task' : 'Mark this task done'}
            >
              {doneState ? <RotateCcw className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
            </Button>
          )}
        </div>

        {doneState ? (
          /* Completed = frozen. Only the reopen button above is live. */
          <div className="rounded-md border border-border bg-secondary/40 px-3 py-2 text-xs text-muted-foreground">
            Completed — locked. Use the reopen button above to edit.
          </div>
        ) : (
          <>
        {/* Type picker — 4 fixed types as segmented buttons. */}
        <div>
          <label className="eyebrow mb-1 block">Task</label>
          <div className="grid grid-cols-2 gap-2">
            {TASK_TYPES.map((t) => {
              const st = taskStyle(t);
              const selected = t === taskType;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTaskType(t)}
                  disabled={busy}
                  className={cn(
                    'flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors',
                    selected
                      ? 'border-primary bg-primary/10 text-foreground font-medium'
                      : 'border-border text-muted-foreground hover:border-primary/40',
                  )}
                >
                  <st.Icon className="h-4 w-4 shrink-0" />
                  {TASK_TYPE_LABELS[t]}
                </button>
              );
            })}
          </div>
        </div>

        <div className="space-y-4 rounded-md border border-border bg-secondary/30 p-4">
          <div>
            <label className="eyebrow mb-1 block">Date</label>
            <Input
              type="date"
              value={taskDate}
              onChange={(e) => setTaskDate(e.target.value)}
              min={dateFloor}
              aria-invalid={dateInvalid}
              className={cn(
                'h-8 text-xs',
                dateInvalid && 'border-destructive focus-visible:ring-destructive',
              )}
              disabled={busy}
            />
            {dateInvalid && (
              <p className="mt-1 text-[0.6875rem] text-destructive">Date can&apos;t be in the past.</p>
            )}
          </div>
          <div>
            <label className="eyebrow mb-1 block">People</label>
            <PeopleMultiSelect value={personIds} onChange={setPersonIds} disabled={busy} />
            <p
              className={cn(
                'mt-1 text-[0.6875rem]',
                noPeople ? 'text-destructive' : 'text-muted-foreground',
              )}
            >
              Select at least one person.
            </p>
          </div>
          <div>
            <label className="eyebrow mb-1 block">Notes (optional)</label>
            <Input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. supplier, contact number…"
              className="h-8 text-xs"
              disabled={busy}
              maxLength={2000}
            />
          </div>
          {saveMutation.error && (
            <div className="text-xs text-destructive">
              {saveMutation.error instanceof Error ? saveMutation.error.message : 'Save failed'}
            </div>
          )}
        </div>
          </>
        )}

        <div className="flex gap-2">
          {!doneState && (
            <Button className="flex-1" disabled={saveDisabled} onClick={() => saveMutation.mutate()}>
              {saveMutation.isPending ? 'Saving…' : isEdit ? 'Save' : 'Create task'}
            </Button>
          )}
          {isEdit && !doneState && (
            <Button
              variant="destructive"
              disabled={busy}
              onClick={() => deleteMutation.mutate()}
              title="Delete task"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          <Button variant="outline" className="flex-1" onClick={onClose}>
            {doneState ? 'Close' : 'Cancel'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
